<template>
  <default-textual-section v-if="$store.state.displayType === 'textual'" :menu="menu" :section="section" :menu-item-component="menuItemComponent"
                           section-menu-type="DINING"
                           class="menu--section-christmas_dining">
    <template v-slot:sectionTitleImage>
      <h2>
        <img src="/library/images/menu/header-christmas-dining-2025.png" alt="Christmas Dining" class="menu--section_logo">
      </h2>
    </template>
    <template v-slot:beforeTopContentArea>
      <h2 tabindex="-1" class="menu--section_title">
        {{ menu.page.page_h1 }}
      </h2>
    </template>
  </default-textual-section>
  <default-visual-section v-else :menu="menu" :section="section"
                          :menu-item-component="menuItemComponent"
                          section-menu-type="DINING"
                          class="menu--section-christmas_dining">
    <template v-slot:sectionTitleImage>
      <h2>
        <img src="/library/images/menu/header-christmas-dining-2025.png" alt="Christmas Dining" class="menu--section_logo">
      </h2>
    </template>
    <template v-slot:beforeTopContentArea>
      <h2 tabindex="-1" class="menu--section_title">
        {{ menu.page.page_h1 }}
      </h2>
    </template>
  </default-visual-section>
</template>

<script lang="ts">
/**
 * Custom component for Christmas Dining default menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 0.1.0
 * @copyright  Copyright (C) 2025-2026, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2025-11-05 (1.0.0) (jhoward)
 *    - Copy from ChristmasDining_defaultSection.vue component to ChristmasDining2025_defaultSection.vue component.
 *    - Update header image in ChristmasDining2025_defaultSection to the 2025 version.
 *    - Update background image in ChristmasDining2025_defaultSection to the 2025 version.
 *    - Customize menu type to "DINING" in ChristmasDining2025_defaultSection.
 *    - Modify styles in ChristmasDining2025_defaultSection to match 2025 branding and layout.
 *
 * @changelog
 *
 * 2026-07-20 (0.1.0) (jhoward)
 *   - Add App\Library\Js\Components\Menu\Section\ChristmasDining2025_defaultSection.vue for Christmas Dining 2025 menu sections.
 */

import MenuTag from "../menu-tag.vue";
import MenuTagLegend from "../menu-tag-legend.vue";
import MenuContentArea from "../menu-content-area.vue";
import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MenuSection} from "../defs";
import MenuItemGroup from "../menu-item-group.vue";
import MenuVersion from "../menu-version.vue";
import MenuSchedules from "../schedule/MenuSchedules.vue";
import DefaultVisualSection from "./visual/defaultVisualSection.vue";
import DefaultTextualSection from "./textual/defaultTextualSection.vue";

export default defineComponent({
  components: {
    DefaultTextualSection, DefaultVisualSection,
    MenuSchedules,
    MenuVersion,
    MenuItemGroup,
    MenuContentArea,
    MenuTag,
    MenuTagLegend,
  },
  name: "ChristmasDining_defaultSection",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: 'christmas-food-item',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
  },
})
</script>

<style>
html .search--container + .visual_menu--page {
  padding-top: 0;
}
</style>

<style scoped>
.menu--section-christmas_dining {
  font-size: 1.3em;
  color: #231f20;
  padding: 6em 1em 10rem;
  font-family: itc-avant-garde-gothic-pro, sans-serif;
}

.menu_switcher--container + .visual_menu--page .menu--section-christmas_dining:first-child {
  padding-top: 10rem;
}

.menu--section-christmas_dining::before {
  content: '';
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('/library/images/menu/background-christmas-dining-2025.jpg');
  background-size: 100% 100%;
  background-position: top center;
}

@media (max-width: 600px) {
  .menu--section-christmas_dining::before {
    background-image: initial;
  }
}

.menu--section-christmas_dining:deep(.menu--highlight) {
  font-weight: bold !important;
}

.menu--section-christmas_dining:deep(.menu--item_title) {
  color: #f1213b;
}

.menu--section-christmas_dining:deep(.menu--item_title-christmas_dining) {
  font-size: 1.2em;
  font-weight: bold;
  letter-spacing: 0.1em;
}

.menu--section-christmas_dining:deep(.menu--item_description h4) {
  color: #000;
}

.menu--section-christmas_dining:deep(h2) {
  padding-left: 0;
}

.menu--section-christmas_dining.textual_menu--section:deep(h2:not(.menu--section_title)) {
  margin-top: -4em;
}

@media (max-width: 780px) {
  .menu--section-christmas_dining.textual_menu--section:deep(h2:not(.menu--section_title)) {
    margin-top: -3em;
  }
}

.menu--section-christmas_dining:deep(h2 + h2.menu--section_title) {
  display: none;
}

.menu--section-christmas_dining:deep(h2 img) {
  width: 65%;
}

.menu--section-christmas_dining:deep(.menu--section_title) {
  color: #000;
  font-weight: 300;
  font-size: 2em !important;
  line-height: 0.85em;
  position: relative;
  padding: 0;
  overflow: initial;
  margin-top: 0.8em;
}

.menu--section-christmas_dining:deep(.menu--section_subtitle) {
  text-align: center;
  font-family: itc-avant-garde-gothic-pro, sans-serif;
  color: #000;
  font-size: 1em;
  line-height: 1.4em;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.menu--section-christmas_dining:deep(.visual_menu--page_section.visual_menu--page_section-top) {
  position: relative;
  font-size: 0.8em;
  font-weight: lighter;
  padding-bottom: 4.1em;
  padding-top: 1.2em;
}

.menu--section-christmas_dining:deep(.visual_menu--page_section.visual_menu--page_section-top p) {
  margin-top: 0;
  margin-bottom: 0;
}

.menu--section-christmas_dining:deep(.visual_menu--page_section-bottom .visual_menu--page_section-bottom) {
  padding-top: 1.25em;
  position: relative;
  font-size: 0.9em;
  font-weight: lighter;
  padding-left: 0;
  padding-right: 0;
  width: 75%;
}

html .menu--section-christmas_dining:deep(.menu--section_menu_type) {
  margin-top: -1.1em;
  font-size: 2em;
  color: #f1213b;
  letter-spacing: 0.3em;
  font-weight: bold;
}

html .menu--section-christmas_dining.textual_menu--section:deep(.menu--section_menu_type) {
  margin-top: -1.7em;
}


@media (max-width: 1024px) {
  html .menu--section-christmas_dining.textual_menu--section:deep(.menu--section_menu_type) {
    margin-top: -1.5em;
  }
}

@media (max-width: 780px) {
  html .menu--section-christmas_dining.textual_menu--section:deep(.menu--section_menu_type) {
    margin-top: -1.3em;
  }
}

.menu--section-christmas_dining:deep(.page-link-group-copy) {
  text-align: center;
  padding-bottom: 1em;
  padding-top: 1em;
  font-size: 1em;
}

.menu--section-christmas_dining:deep(.page-link-group-copy.menu--page_content-bottom) {
  padding-top: 4em;
}

.menu--section-christmas_dining.textual_menu--section.body-container:deep(.viewport){
  width: 70%;
}

.menu--section-christmas_dining:deep(.menu--item_group) {
  width: 70%;
  margin: 0 auto;
}

@media (max-width: 780px) {
  .menu--section-christmas_dining.textual_menu--section.body-container:deep(.viewport),
  .menu--section-christmas_dining:deep(.menu--item_group) {
    width: 100%;
  }
}
</style>